import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import {
  CloudFrontSigner,
  toCloudFrontSignerParameters,
} from '../../utils/cloud-front-signer/cloud-front-signer';
import { CesiumStore } from './cesium-point-cloud-store/cesium-point-cloud-store';
import { SignedCloudFrontCesiumStore } from './cesium-point-cloud-store/signed-cloud-front-url-point-cloud-store';
import { OrthoTileStore } from './ortho-tile-store/ortho-tile-store';
import { SignedCloudFrontUrlOrthoTileStore } from './ortho-tile-store/signed-cloud-front-url-ortho-tile-store';
import { PointCloudStore } from './point-cloud-store/point-cloud-store';
import { SignedCloudFrontPointCloudStore } from './point-cloud-store/signed-cloud front-url-point-cloud-store';
import { ScanStore } from './scan-store/scan-store';
import { SignedCloudFrontUrlScanStore } from './scan-store/signed-cloud-front-url-scan-store';

interface SharedViewData {
  sharedViewId: string;
}

export class GeodataStoreFactory {
  static get instance() {
    return GeodataStoreFactory._instance;
  }

  private static _instance = new GeodataStoreFactory();

  private sharedViewData?: SharedViewData;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  setSharedViewId(sharedViewId: string) {
    this.sharedViewData = { sharedViewId };
  }

  async createScanStore(scanId: string): Promise<ScanStore> {
    let signedRequestResponse;
    /**
     *  If shared view is defined, make request to shared viewer endpoint.
     */
    if (this.sharedViewData) {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      signedRequestResponse = await sharedViewService.getScanSignedRequest({
        path: { id: this.sharedViewData.sharedViewId, scanId },
      });
    } else {
      const scanService = SkyMapAxiosServiceFactory.instance.createScanServiceV0();
      signedRequestResponse = await scanService.fetchSignRequestParameters({
        path: { scanId },
      });
    }

    return new SignedCloudFrontUrlScanStore(
      new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse)),
      scanId,
    );
  }

  async createTiledModelStore(tiledModelId: string): Promise<CesiumStore> {
    let signedRequestResponse;
    /**
     *  If shared view is defined, make request to shared viewer endpoint.
     */
    if (this.sharedViewData) {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      signedRequestResponse = await sharedViewService.getTiledModelSignedRequest({
        path: { sharedViewId: this.sharedViewData.sharedViewId, tiledModelId },
      });
    } else {
      const tiledModelService = SkyMapAxiosServiceFactory.instance.createTiledModelServiceV1();
      signedRequestResponse = await tiledModelService.getSignedRequestParameters({
        path: { tiledModelId },
      });
    }

    return new SignedCloudFrontCesiumStore(
      new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse)),
    );
  }

  async createTiledModelCloudFrontSigner(tiledModelId: string) {
    let signedRequestResponse;
    /**
     *  If shared view is defined, make request to shared viewer endpoint.
     */
    if (this.sharedViewData) {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      signedRequestResponse = await sharedViewService.getTiledModelSignedRequest({
        path: { sharedViewId: this.sharedViewData.sharedViewId, tiledModelId },
      });
    } else {
      const tiledModelService = SkyMapAxiosServiceFactory.instance.createTiledModelServiceV1();
      signedRequestResponse = await tiledModelService.getSignedRequestParameters({
        path: { tiledModelId },
      });
    }

    return new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse));
  }

  async createCesiumPointCloudCloudFrontSigner(pointCloudId: string) {
    let signedRequestResponse;
    if (this.sharedViewData) {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      signedRequestResponse = await sharedViewService.getPointCloudSignedRequest({
        path: { id: this.sharedViewData.sharedViewId, pointCloudId },
        query: { isCesium: true },
      });
    } else {
      const pointCloudService = SkyMapAxiosServiceFactory.instance.createPointCloudServiceV0();
      signedRequestResponse = await pointCloudService.fetchSignRequestParameters({
        path: { pointCloudId },
        query: { isCesium: true },
      });
    }

    return new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse));
  }

  /**
   * Signed URL of a COPC point cloud's file. The signature covers only that file, which is read with
   * HTTP range requests.
   */
  async createCopcPointCloudUrl(pointCloudId: string): Promise<string> {
    const signer = await this.createCesiumPointCloudCloudFrontSigner(pointCloudId);
    return signer.getSignedRequest('');
  }

  async createCesiumPointCloudStore(pointCloudId: string): Promise<CesiumStore> {
    let signedRequestResponse;
    /**
     *  If shared view is defined, make request to shared viewer endpoint.
     */
    if (this.sharedViewData) {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      signedRequestResponse = await sharedViewService.getPointCloudSignedRequest({
        path: { id: this.sharedViewData.sharedViewId, pointCloudId },
        query: { isCesium: true },
      });
    } else {
      const pointCloudService = SkyMapAxiosServiceFactory.instance.createPointCloudServiceV0();
      signedRequestResponse = await pointCloudService.fetchSignRequestParameters({
        path: { pointCloudId },
        query: { isCesium: true },
      });
    }
    return new SignedCloudFrontCesiumStore(
      new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse)),
    );
  }

  async createPointCloudStore(scanId: string, pointCloudName: string): Promise<PointCloudStore> {
    let signedRequestResponse;
    /**
     *  If shared view is defined, make request to shared viewer endpoint.
     */
    if (this.sharedViewData) {
      const sharedViewService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      signedRequestResponse = await sharedViewService.getPointCloudSignedRequest({
        path: { id: this.sharedViewData.sharedViewId, pointCloudId: scanId },
        query: { isCesium: false },
      });
    } else {
      const pointCloudService = SkyMapAxiosServiceFactory.instance.createPointCloudServiceV0();
      signedRequestResponse = await pointCloudService.fetchSignRequestParameters({
        path: { pointCloudId: scanId },
        query: { isCesium: false },
      });
    }
    return new SignedCloudFrontPointCloudStore(
      new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse)),
      scanId,
    );
  }

  async createOrthoTileStore(orthoTileName: string): Promise<OrthoTileStore> {
    return new SignedCloudFrontUrlOrthoTileStore(await this.createOrthoTileSigner(orthoTileName));
  }

  /**
   * Signed URL of a COG orthotile's GeoTIFF. The signature covers only that file, which is
   * read with HTTP range requests.
   */
  async createOrthoTileCogUrl(orthoTileName: string): Promise<string> {
    const signer = await this.createOrthoTileSigner(orthoTileName);
    return signer.getSignedRequest('');
  }

  private async createOrthoTileSigner(orthoTileName: string): Promise<CloudFrontSigner> {
    let signedRequestResponse;
    /**
     *  If shared view is defined, make request to shared viewer endpoint.
     */
    if (this.sharedViewData) {
      const orthoTileService = SkyMapAxiosServiceFactory.instance.createSharedViewServiceV0();
      signedRequestResponse = await orthoTileService.getOrthoTileSignedRequest({
        path: { id: this.sharedViewData.sharedViewId, orthoTileId: orthoTileName },
      });
    } else {
      const orthoTileService = SkyMapAxiosServiceFactory.instance.createOrthoTileServiceV0();
      signedRequestResponse = await orthoTileService.fetchSignRequestParameters({
        path: { orthoTileId: orthoTileName },
      });
    }

    return new CloudFrontSigner(toCloudFrontSignerParameters(signedRequestResponse));
  }
}
